import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiRefreshCw,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
  useParams,
} from "react-router-dom";

import {
  fetchQuotationById,
  fetchQuotationHistory,
  quotationHistorySelector,
  quotationLoadingSelector,
  selectedQuotationSelector,
} from "../../../../../../store/billing";

import "./QuotationHistoryPage.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getNestedValue = (
  object,
  path,
  fallback = ""
) => {
  if (!object || !path) {
    return fallback;
  }

  const value = String(path)
    .split(".")
    .reduce(
      (result, key) =>
        result?.[key],
      object
    );

  return value === undefined ||
    value === null ||
    value === ""
    ? fallback
    : value;
};

const getFirstValue = (
  object,
  paths = [],
  fallback = ""
) => {
  for (const path of paths) {
    const value =
      getNestedValue(
        object,
        path,
        undefined
      );

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return fallback;
};

const formatDateTime = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
};

const formatLabel = (
  value
) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const getActionClass = (
  action
) => {
  switch (
    String(action || "")
      .toUpperCase()
  ) {
    case "CREATED":
    case "CREATE":
      return "quotation-history-action-created";

    case "UPDATED":
    case "UPDATE":
      return "quotation-history-action-updated";

    case "CONFIRMED":
    case "CONFIRM":
      return "quotation-history-action-confirmed";

    case "CANCELLED":
    case "CANCEL":
      return "quotation-history-action-cancelled";

    case "DUPLICATED":
    case "DUPLICATE":
      return "quotation-history-action-duplicated";

    case "CONVERTED":
    case "CONVERT":
      return "quotation-history-action-converted";

    case "DELETED":
    case "DELETE":
      return "quotation-history-action-deleted";

    default:
      return "quotation-history-action-default";
  }
};

const getQuotationNumber = (
  quotation
) => {
  return getFirstValue(
    quotation,
    [
      "documentNumber",
      "quotationNumber",
      "quotationNo",
      "number",
    ],
    "Quotation"
  );
};

/*
|--------------------------------------------------------------------------
| QUOTATION HISTORY PAGE
|--------------------------------------------------------------------------
*/

const QuotationHistoryPage = () => {
  const dispatch =
    useDispatch();

  const history =
    useHistory();

  const { id } =
    useParams();

  const quotation =
    useSelector(
      selectedQuotationSelector
    );

  const historyState =
    useSelector(
      quotationHistorySelector
    ) || {};

  const loadingState =
    useSelector(
      quotationLoadingSelector
    ) || {};

  const [
    filters,
    setFilters,
  ] = useState({
    action: "",
    page: 1,
    limit: 10,
  });

  const historyRecords =
    useMemo(() => {
      if (
        Array.isArray(
          historyState
        )
      ) {
        return historyState;
      }

      return (
        historyState.history ||
        historyState.records ||
        historyState.data ||
        []
      );
    }, [historyState]);

  const pagination =
    useMemo(
      () => ({
        page:
          Number(
            historyState
              ?.pagination
              ?.page ||
            filters.page ||
            1
          ),

        limit:
          Number(
            historyState
              ?.pagination
              ?.limit ||
            filters.limit ||
            10
          ),

        total:
          Number(
            historyState
              ?.pagination
              ?.total ||
            historyRecords.length ||
            0
          ),

        totalPages:
          Math.max(
            Number(
              historyState
                ?.pagination
                ?.totalPages ||
              Math.ceil(
                Number(
                  historyState
                    ?.pagination
                    ?.total ||
                  historyRecords.length ||
                  0
                ) /
                  Number(
                    historyState
                      ?.pagination
                      ?.limit ||
                    filters.limit ||
                    10
                  )
              ) ||
              1
            ),
            1
          ),
      }),
      [
        historyState,
        historyRecords.length,
        filters.page,
        filters.limit,
      ]
    );

  const loading =
    Boolean(
      loadingState
        .historyLoading ||
      loadingState
        .detailsLoading ||
      loadingState
        .fetchByIdLoading
    );

  const actionOptions = [
    "",
    "CREATED",
    "UPDATED",
    "CONFIRMED",
    "CANCELLED",
    "DUPLICATED",
    "CONVERTED",
    "DELETED",
  ];

  /*
  |--------------------------------------------------------------------------
  | LOAD HISTORY
  |--------------------------------------------------------------------------
  */

  const loadHistory = (
    overrides = {}
  ) => {
    const requestFilters = {
      ...filters,
      ...overrides,
    };

    dispatch(
      fetchQuotationHistory({
        documentId: id,
        params: requestFilters,
      })
    );
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(
      fetchQuotationById(
        id
      )
    );

    dispatch(
      fetchQuotationHistory({
        documentId: id,
        params: {
          page: 1,
          limit: 10,
        },
      })
    );
  }, [
    dispatch,
    id,
  ]);

  /*
  |--------------------------------------------------------------------------
  | HANDLERS
  |--------------------------------------------------------------------------
  */

  const handleActionChange = (
    event
  ) => {
    const action =
      event.target.value;

    const nextFilters = {
      ...filters,
      action,
      page: 1,
    };

    setFilters(
      nextFilters
    );

    loadHistory(
      nextFilters
    );
  };

  const handleLimitChange = (
    event
  ) => {
    const limit =
      Number(
        event.target.value
      ) || 10;

    const nextFilters = {
      ...filters,
      page: 1,
      limit,
    };

    setFilters(
      nextFilters
    );

    loadHistory(
      nextFilters
    );
  };

  const handlePageChange = (
    page
  ) => {
    if (
      page < 1 ||
      page >
        pagination.totalPages
    ) {
      return;
    }

    const nextFilters = {
      ...filters,
      page,
    };

    setFilters(
      nextFilters
    );

    loadHistory(
      nextFilters
    );
  };

  const handleRefresh = () => {
    loadHistory();
  };

  const getSerialNumber = (
    index
  ) => {
    return (
      (
        pagination.page -
        1
      ) *
        pagination.limit +
      index +
      1
    );
  };

  const getActorName = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "actor.name",
        "actor.fullName",
        "performedBy.name",
        "performedBy.fullName",
        "createdBy.name",
        "createdBy.fullName",
        "user.name",
        "user.fullName",
        "actorName",
      ],
      "System"
    );
  };

  const getActorRole = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "actor.role",
        "performedBy.role",
        "createdBy.role",
        "user.role",
        "actorRole",
      ],
      ""
    );
  };

  const getActionText = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "action",
        "actionType",
        "event",
        "activityType",
      ],
      "UPDATED"
    );
  };

  const getDescription = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "description",
        "message",
        "remarks",
        "note",
        "details.message",
      ],
      `${formatLabel(
        getActionText(
          record
        )
      )} quotation`
    );
  };

  const getIpAddress = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "request.ip",
        "request.ipAddress",
        "ipAddress",
        "ip",
      ],
      "-"
    );
  };

  const getCreatedDate = (
    record
  ) => {
    return getFirstValue(
      record,
      [
        "createdAt",
        "actionDate",
        "timestamp",
        "date",
      ],
      ""
    );
  };

  return (
    <div className="quotation-history-page">
      <div className="quotation-history-header">
        <div className="quotation-history-heading">
          <button
            type="button"
            className="quotation-history-back-button"
            onClick={() =>
              history.push(
                `/billing/quotations/${id}`
              )
            }
          >
            <FiArrowLeft />
          </button>

          <div className="quotation-history-heading-icon">
            <FiClock />
          </div>

          <div>
            <h1>
              Quotation History
            </h1>

            <p>
              View all activities performed on{" "}
              <strong>
                {getQuotationNumber(
                  quotation
                )}
              </strong>
              .
            </p>
          </div>
        </div>

        <button
          type="button"
          className="quotation-history-refresh-button"
          onClick={
            handleRefresh
          }
          disabled={
            loading
          }
        >
          <FiRefreshCw
            className={
              loading
                ? "quotation-history-spin"
                : ""
            }
          />

          Refresh
        </button>
      </div>

      <div className="quotation-history-filter-card">
        <div className="quotation-history-filter-field">
          <label htmlFor="quotation-history-action">
            Action
          </label>

          <select
            id="quotation-history-action"
            value={
              filters.action
            }
            onChange={
              handleActionChange
            }
            disabled={
              loading
            }
          >
            {actionOptions.map(
              (action) => (
                <option
                  key={
                    action ||
                    "ALL"
                  }
                  value={
                    action
                  }
                >
                  {action
                    ? formatLabel(
                        action
                      )
                    : "All Actions"}
                </option>
              )
            )}
          </select>
        </div>

        <div className="quotation-history-filter-summary">
          <FiFileText />

          <span>
            {pagination.total} history record
            {pagination.total !== 1
              ? "s"
              : ""}
          </span>
        </div>
      </div>

      <div className="quotation-history-table-card">
        <div className="quotation-history-table-scroll">
          <table className="quotation-history-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>
                  S.No
                </th>

                <th>
                  Action
                </th>

                <th>
                  Description
                </th>

                <th>
                  Performed By
                </th>

                <th>
                  IP Address
                </th>

                <th>
                  Date & Time
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="quotation-history-table-message"
                  >
                    <div className="quotation-history-loading">
                      <span className="quotation-history-spinner" />

                      Loading quotation history...
                    </div>
                  </td>
                </tr>
              ) : historyRecords.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="quotation-history-table-message"
                  >
                    <div className="quotation-history-empty">
                      <FiClock />

                      <strong>
                        No History Found
                      </strong>

                      <span>
                        No activity records are
                        available for this
                        quotation.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                historyRecords.map(
                  (
                    record,
                    index
                  ) => {
                    const action =
                      getActionText(
                        record
                      );

                    const actorName =
                      getActorName(
                        record
                      );

                    const actorRole =
                      getActorRole(
                        record
                      );

                    return (
                      <tr
                        key={
                          record?._id ||
                          record?.id ||
                          index
                        }
                      >
                        <td className="quotation-history-serial">
                          {getSerialNumber(
                            index
                          )}
                        </td>

                        <td>
                          <span
                            className={`quotation-history-action ${getActionClass(
                              action
                            )}`}
                          >
                            {formatLabel(
                              action
                            )}
                          </span>
                        </td>

                        <td>
                          <div className="quotation-history-description">
                            <strong>
                              {getDescription(
                                record
                              )}
                            </strong>

                            {record?.reason && (
                              <span>
                                Reason:{" "}
                                {record.reason}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="quotation-history-actor">
                            <div className="quotation-history-avatar">
                              {String(
                                actorName
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <strong>
                                {actorName}
                              </strong>

                              <span>
                                {actorRole ||
                                  "User"}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <code className="quotation-history-ip">
                            {getIpAddress(
                              record
                            )}
                          </code>
                        </td>

                        <td>
                          <span className="quotation-history-date">
                            {formatDateTime(
                              getCreatedDate(
                                record
                              )
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>
          </table>
        </div>

        {!loading &&
        pagination.total > 0 ? (
          <div className="quotation-history-pagination">
            <div className="quotation-history-pagination-summary">
              Showing{" "}
              <strong>
                {(pagination.page -
                  1) *
                  pagination.limit +
                  1}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(
                  pagination.page *
                    pagination.limit,
                  pagination.total
                )}
              </strong>{" "}
              of{" "}
              <strong>
                {pagination.total}
              </strong>{" "}
              records
            </div>

            <div className="quotation-history-pagination-controls">
              <select
                value={
                  pagination.limit
                }
                onChange={
                  handleLimitChange
                }
                disabled={
                  loading
                }
              >
                <option value={10}>
                  10 / page
                </option>

                <option value={20}>
                  20 / page
                </option>

                <option value={50}>
                  50 / page
                </option>

                <option value={100}>
                  100 / page
                </option>
              </select>

              <button
                type="button"
                disabled={
                  pagination.page <=
                  1
                }
                onClick={() =>
                  handlePageChange(
                    pagination.page -
                      1
                  )
                }
                title="Previous page"
              >
                <FiChevronLeft />
              </button>

              <span>
                Page{" "}
                <strong>
                  {pagination.page}
                </strong>{" "}
                of{" "}
                <strong>
                  {
                    pagination.totalPages
                  }
                </strong>
              </span>

              <button
                type="button"
                disabled={
                  pagination.page >=
                  pagination.totalPages
                }
                onClick={() =>
                  handlePageChange(
                    pagination.page +
                      1
                  )
                }
                title="Next page"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default QuotationHistoryPage;